import React, { useEffect } from 'react';
import Navbar from '../components/storefront/Navbar';
import Footer from '../components/storefront/Footer';
import { TestimonialCarousel } from '../components/ui/profile-card-testimonial-carousel';
import { FiSun, FiHeart, FiCheckCircle, FiGlobe, FiAward } from 'react-icons/fi';

const TEAM_MEMBERS = [
  {
    id: '1',
    name: 'Minoli Gunasinghe',
    role: 'Founder & CEO • M.Sc. Food Science',
    image: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1780203988/team-minoli.jpg_esh7gu.png',
    bio: 'Having grown up recognizing the importance of promoting good culinary practices, my academic journey led me to pursue a Science degree at the University of Colombo. Subsequently, I furthered my studies with a postgraduate degree in the Republic of Korea, delving into the realms of food biotechnology and sensory evaluations. With a vision to introduce export-quality products to Sri Lankans and foster a culture of healthy eating in the country, I am committed to this goal. Join us in our forward-looking mission!',
  },
  {
    id: '2',
    name: 'Kaushal Kulasekera',
    role: 'Co-Founder & Director • Mechatronics Eng.',
    image: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1782466924/e58d9716a8f56306f1408b1f65141821_pwny92.webp',
    bio: 'I am deeply committed to integrating contemporary technology with time-honored methods to enhance efficiency in various processes. Being a graduate of Asia Institute of Technology (Thailand), I got the exposure for innovative, more entrepreneurial, and more economically relevant researches that are leveraging on its social impact core strengths. Join us in breaking down barriers and uncovering innovative solutions!',
  },
  {
    id: '3',
    name: 'Charith Fernando',
    role: 'Director of Marketing & Business Analyst',
    image: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1780203045/team-charith.jpg_itbuu3.png',
    bio: 'My journey in this business is driven by an unwavering passion for Ceylonese Foods & tea passion that transcends the boundaries of time itself. Drawing inspiration from centuries-old traditions, I am dedicated to infusing our offerings with a contemporary flair, resulting in a novel experience that seamlessly marries the timeless essence of ancient rituals with the innovative spirit of the modern world. It’s this delicate balance between tradition and innovation that defines our commitment to crafting a tea experience that is both enduringly timeless and refreshingly cutting-edge.',
  },
  {
    id: '4',
    name: 'Anuruddhika Rathnayake',
    role: 'Food Consultant • PhD candidate',
    image: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1782466922/ff7b357089e60ff8a1bfadd234430cf4_bimusn.webp',
    bio: 'As a dedicated researcher and PhD candidate affiliated with the Centre for Nutrition and Food Sciences (CNAFS) and the Queensland Alliance for Agriculture and Food Innovation (QAAFI) at the University of Queensland, I have firsthand experience witnessing how cutting-edge technologies are revolutionizing food production and consumption, all while prioritizing safety and quality. Join us in savoring top-tier food products.',
  },
  {
    id: '5',
    name: 'Park Byoung Jun',
    role: 'Export Manager',
    image: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1782466880/a84258cf5155b378bfa21f5363791149_qdwb1u.webp',
    bio: 'I am proud to introduce the enchanting flavors of Sri Lankan tea to enthusiasts worldwide. Rooted in the rich traditions of my Korean heritage, I bring a unique perspective to our tea offerings, infusing them with the spirit of harmony and balance. By partnering with Sri Lankan tea artisans, we blend the essence of two vibrant cultures, creating a fusion that tantalizes the senses and transcends borders. Join us on this journey of discovery as we bridge continents through the universal language of tea.',
  },
];

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const teamCarouselData = TEAM_MEMBERS.map((member) => ({
    name: member.name,
    title: member.role,
    description: member.bio,
    imageUrl: member.image,
  }));

  return (
    <div className="bg-brand-sand min-h-screen pt-24 font-sans">
      <Navbar />

      {/* Section 1: Welcome to Ceylanthro */}
      <section className="py-16 md:py-24 px-6 md:px-12 max-w-7xl mx-auto relative overflow-hidden">
        <div className="size-[300px] md:size-[520px] rounded-full absolute blur-[150px] -z-10 bg-brand-gold/20 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></div>
        
        <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-20">
          <div className="flex-1 w-full relative group">
            <div className="absolute inset-0 bg-brand-darkSage/10 rounded-[2rem] transform -rotate-3 scale-105 -z-10 transition-transform duration-500 group-hover:rotate-0"></div>
            <img 
              className="w-full rounded-[2rem] h-auto object-cover aspect-[4/5] shadow-2xl transition-transform duration-700 group-hover:scale-[1.02]" 
              src="https://res.cloudinary.com/dfg8nrgvf/image/upload/v1782465759/About-us-pic5-1-scaled_d50d8j.jpg" 
              alt="Welcome to Ceylanthro" 
            />
          </div>

          <div className="flex-1 w-full">
            <p className="text-brand-darkSage tracking-[0.2em] font-semibold text-xs mb-4 uppercase">Our Story</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-darkSage leading-tight mb-8">
              Welcome to Ceylanthro!
            </h1>
            <p className="text-base md:text-lg text-brand-lightText leading-relaxed mb-10">
              At Ceylanthro, we are more than just a brand; we are purveyors of nature’s finest treasures, dedicated to enhancing your well-being and delighting your senses. Our journey began with a deep-rooted passion for the richness and diversity of Sri Lanka’s natural bounty, and a desire to share its wonders with the world.
            </p>

            <div className="flex flex-col gap-8">
              <div className="flex items-start gap-5">
                <div className="size-12 shrink-0 flex items-center justify-center bg-white border border-brand-gold/30 rounded-2xl shadow-sm text-brand-gold">
                  <FiSun className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-bold text-brand-darkSage">Nature's Finest</h3>
                  <p className="text-brand-lightText mt-2 leading-relaxed">Carefully cultivated in the rich, fertile soils of Sri Lanka, bringing authentic vitality to your daily routine.</p>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="size-12 shrink-0 flex items-center justify-center bg-white border border-brand-gold/30 rounded-2xl shadow-sm text-brand-gold">
                  <FiHeart className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-bold text-brand-darkSage">Holistic Well-being</h3>
                  <p className="text-brand-lightText mt-2 leading-relaxed">Products intentionally designed to nourish your body, mind, and soul through time-tested natural remedies.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1.5: About Ceylon Spices (Updated for Full Size Image) */}
      <section className="py-16 md:py-24 px-6 md:px-12 max-w-7xl mx-auto relative overflow-hidden">
        <div className="flex flex-col lg:flex-row-reverse items-center justify-center gap-12 lg:gap-20">
          {/* Image Side - Removed aspect ratio and object-cover to show full image */}
          <div className="flex-1 w-full relative group">
            <div className="absolute inset-0 bg-brand-gold/10 rounded-[2rem] transform rotate-3 scale-105 -z-10 transition-transform duration-500 group-hover:rotate-0"></div>
            <img 
              className="w-full rounded-[2rem] h-auto shadow-2xl transition-transform duration-700 group-hover:scale-[1.02]" 
              src="https://res.cloudinary.com/dfg8nrgvf/image/upload/v1782904372/Visual_j4cavt.png" 
              alt="About Ceylon Spices" 
            />
          </div>

          {/* Text Side */}
          <div className="flex-1 w-full">
            <p className="text-brand-darkSage tracking-[0.2em] font-semibold text-xs mb-4 uppercase">Our Heritage</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-darkSage leading-tight mb-8">
              About Ceylon Spices
            </h2>
            <p className="text-base md:text-lg text-brand-lightText leading-relaxed mb-6">
              Discover the authentic flavors of Sri Lanka with Ceylanthro's premium Ceylon spices. Carefully sourced from the island's finest growing regions and expertly processed to preserve their natural aroma, flavor, and freshness, our spices bring exceptional quality to every meal.
            </p>
            <p className="text-base md:text-lg text-brand-lightText leading-relaxed">
              From world-renowned Ceylon cinnamon to aromatic pepper, cloves, cardamom, and more, each product reflects Sri Lanka's rich spice heritage and our commitment to excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2: Meet the Team */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#e8e4d9] relative border-y border-brand-darkSage/5 overflow-hidden">
        <div className="size-[400px] md:size-[600px] bottom-0 right-0 translate-x-1/3 translate-y-1/3 rounded-full absolute blur-[200px] -z-10 bg-brand-gold/10"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 md:mb-20">
            <p className="text-brand-darkSage tracking-[0.2em] font-semibold text-xs mb-4 uppercase">The Minds Behind The Magic</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-darkSage mb-6">Meet the Team</h2>
            <p className="text-base md:text-lg text-brand-lightText max-w-2xl mx-auto leading-relaxed">
              Our diverse team of experts combines centuries of traditional wisdom with modern science to bring you the true essence of Ceylon.
            </p>
          </div>

          <TestimonialCarousel testimonials={teamCarouselData} />
        </div>
      </section>

      {/* Section 3: Our Promise */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-white/40 relative">
        <div className="size-[400px] md:size-[600px] top-0 left-1/2 -translate-x-1/2 rounded-full absolute blur-[200px] -z-10 bg-brand-sage/10"></div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <p className="text-brand-darkSage tracking-[0.2em] font-semibold text-xs mb-4 uppercase">Our Commitment</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-darkSage mb-8">Our Promise</h2>
            <p className="text-base md:text-lg text-brand-lightText max-w-3xl mx-auto leading-relaxed">
              When you choose Ceylanthro, you’re not just selecting a product; you’re inviting a piece of Sri Lanka’s vibrant culture and natural beauty into your home. With every sip of tea and every application of oil, we promise to deliver an experience that transcends the ordinary, leaving you refreshed, revitalized, and ready to embrace life’s extraordinary moments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16">
            <div className="group bg-brand-sand/50 p-8 rounded-3xl border border-brand-darkSage/5 hover:bg-white transition-all duration-300 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)]">
              <div className="size-14 mb-8 flex items-center justify-center bg-white border border-brand-gold/20 rounded-2xl text-brand-gold group-hover:-translate-y-2 group-hover:shadow-lg group-hover:shadow-brand-gold/20 transition-all duration-300">
                <FiCheckCircle className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-brand-darkSage mb-4">Uncompromising Quality</h3>
              <p className="text-brand-lightText leading-relaxed">Every product undergoes rigorous testing to ensure it meets our exacting standards for purity, potency, and authentic flavor.</p>
            </div>

            <div className="group bg-brand-sand/50 p-8 rounded-3xl border border-brand-darkSage/5 hover:bg-white transition-all duration-300 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)]">
              <div className="size-14 mb-8 flex items-center justify-center bg-white border border-brand-gold/20 rounded-2xl text-brand-gold group-hover:-translate-y-2 group-hover:shadow-lg group-hover:shadow-brand-gold/20 transition-all duration-300">
                <FiGlobe className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-brand-darkSage mb-4">Sustainable Sourcing</h3>
              <p className="text-brand-lightText leading-relaxed">We work directly with local farmers, ensuring fair trade practices, community empowerment, and strict environmental stewardship.</p>
            </div>

            <div className="group bg-brand-sand/50 p-8 rounded-3xl border border-brand-darkSage/5 hover:bg-white transition-all duration-300 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)]">
              <div className="size-14 mb-8 flex items-center justify-center bg-white border border-brand-gold/20 rounded-2xl text-brand-gold group-hover:-translate-y-2 group-hover:shadow-lg group-hover:shadow-brand-gold/20 transition-all duration-300">
                <FiAward className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-brand-darkSage mb-4">Authentic Heritage</h3>
              <p className="text-brand-lightText leading-relaxed">Our methods honor centuries of traditional Sri Lankan craftsmanship and Ayurvedic wisdom, preserving the true essence of Ceylon.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;